import React, { useRef, useState } from 'react';
import HoneyPot, { IHoneyPot } from '../../Shared/HoneyPot';
import { LanguageService } from '../../../services/language.service';
import ValidationInfo from '../../../validation/validation-info';
import TextFieldFormGroup, { ITextFieldFormGroupState } from '../../Shared/TextFieldFormGroup';
import LogService from '../../../services/log-service/log.service';
import AntiforgeryService from '../../../services/anti-forgery.service';
import { IContactFormApiModel } from './models/contact-form.api-model';
import Alert from '../../Shared/Alert';
import IValidatable, { IServerValidationError } from '../../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../helpers/validation.helper';
import { AnalyticsService } from '../../../services/analytics-service';
import ValidationSummary from '../../Shared/ValidationSummary';
import ContainerItemBlockHeading from '../../Shared/ContainerItemBlockHeading';
import { IFormBaseSettings } from '../form-base-props.interface';
import { useTexts } from '../../../hooks/texts.hook';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';

export interface IContactFormTexts {
  heading: string;
  submitted: string;
  submit: string;
  formsRequiredFieldsMessageHtml: string;
}

export type IContactFormSettings = IFormBaseSettings;

type IContactFormProps = IServerSideRendered<IContactFormTexts, IContactFormSettings>;

function ContactForm({ settings, texts }: IContactFormProps) {
  // === HOOKS ===
  const { t } = useTexts();

  // === STATE ===
  const [nameState, setNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [emailState, setEmailState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [messageState, setMessageState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [show, setShow] = useState(true);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // === REFS ===
  const honeyPotRef = useRef<IHoneyPot>(null!);
  const nameRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);
  const messageRef = useRef<IValidatable>(null!);

  // === VARIABLES ===
  const isRequiredFieldsValid = !!nameState.value && !!emailState.value && !!messageState.value;
  const showSubmitButton = settings.alwaysShowSubmitButton || isRequiredFieldsValid;
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: nameRef.current, validationInfo: nameState.validationInfo, setState: setNameState },
      { component: emailRef.current, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: messageRef.current, validationInfo: messageState.validationInfo, setState: setMessageState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.Forms, AnalyticsService.ErrorAction, `Kontaktformulär - ${e}`);
    });
  }

  // === EVENT HANDLERS ===
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting || !honeyPotRef.current.isValid()) return;

    const [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
        AnalyticsService.addEvent(AnalyticsService.Forms, AnalyticsService.ErrorAction, `Kontaktformulär - ${e}`);
      });
      return;
    }

    setIsSubmitting(true);
    setShowErrorMessage(false);

    try {
      const requestData: IContactFormApiModel = {
        $type: 'Contact',
        blockContentGuid: settings.blockContentGuid,
        pageContentGuid: settings.pageContentGuid,
        name: nameState.value,
        email: emailState.value,
        message: messageState.value,
      };

      const formData = new FormData();
      formData.append('feedbackForm', JSON.stringify(requestData));

      const response = await fetch(`/api/forms/submit?language=${LanguageService.getCurrentLanguage()}`, {
        method: 'POST',
        headers: { 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 400) {
          handleServerValidationErrors((await response.json()).errors);
          setTimeout(() => {
            document.getElementById('contact-form-validation-summary')?.focus();
          }, 100);
          return;
        } else {
          throw new Error('Failed to submit contact form');
        }
      }

      setIsSubmitted(true);
    } catch (error) {
      LogService.logError(error);
      setShowErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={`contact-form ${settings.useBorder ? 'border p-4 rounded' : ''} position-relative ${settings.colorTheme} ${show ? '' : 'd-none'} ${settings.className ?? ''}`}>
      {settings.useCloseButton && <button type="button" className="btn-close position-absolute top-0 end-0" aria-label={t.close} onClick={() => setShow(false)} />}

      <div aria-live="polite" role="status">
        {isSubmitted && <p className="mb-0">{texts.submitted}</p>}
      </div>
      {!isSubmitted && (
        <>
          <ContainerItemBlockHeading
            headingText={texts.heading}
            headingLevel={settings.headingLevel}
            headingLevelClass={settings.headingLevelClass}
            headingVisuallyHidden={settings.headingVisuallyHidden}
          />
          <div className="mb-3" role="alert" aria-live="polite">
            {texts.formsRequiredFieldsMessageHtml}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <ValidationSummary id="contact-form-validation-summary" validationSummaryErrors={validationSummaryErrors} className={validationSummaryErrors.length > 0 ? 'mb-3' : ''} />
              <TextFieldFormGroup
                ref={nameRef}
                serverValidationName="name"
                labelText={t('För och efternamn', 'First and last name')}
                inputId={`name-${settings.blockContentGuid}`}
                autocomplete="off"
                maxLength={100}
                renderAsTextArea={false}
                required={true}
                state={nameState}
                onChange={(value) => {
                  setNameState({ ...nameState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={emailRef}
                serverValidationName="email"
                labelText={t.email}
                inputId={`email-${settings.blockContentGuid}`}
                autocomplete="email"
                renderAsTextArea={false}
                required={true}
                inputMode="email"
                maxLength={100}
                state={emailState}
                onChange={(value) => {
                  setEmailState({ ...emailState, value });
                }}
              />
            </div>

            <div className="mb-3">
              <TextFieldFormGroup
                ref={messageRef}
                serverValidationName="message"
                labelText={t('Ställ din fråga', 'Ask your question')}
                inputId={`message-${settings.blockContentGuid}`}
                autocomplete="off"
                renderAsTextArea={true}
                required={true}
                rows={3}
                state={messageState}
                maxLength={2000}
                onChange={(value) => {
                  setMessageState({ ...messageState, value });
                }}
              />
            </div>
            <Alert show={showErrorMessage} heading={t.generalError} closable={false} className="mb-3" />
            <HoneyPot ref={honeyPotRef} />
            {showSubmitButton && (
              <button type="submit" className="btn btn-primary w-100" disabled={!isRequiredFieldsValid || isSubmitting}>
                {texts.submit}
              </button>
            )}
          </form>
        </>
      )}
    </div>
  );
}

export default ContactForm;